import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  BarChart3, Users, MessageSquareText, TrendingUp, Send, RefreshCw,
  CheckCircle2, Circle, Briefcase, Target, Award, Loader2, AlertTriangle,
  ChevronDown, ChevronUp, MailCheck,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const F1_LABELS = {
  vollzeit: "Vollzeit", teilzeit: "Teilzeit", minijob: "Minijob",
  ausbildung: "Ausbildung", arbeitssuchend: "Arbeitssuchend",
  andere_massnahme: "Andere Qualifizierung",
};
const F2_LABELS = {
  ja_vollstaendig: "Ja, vollständig", teilweise: "Teilweise",
  eher_nicht: "Eher nicht", nicht_zutreffend: "Nicht zutreffend",
};
const F3_LABELS = {
  ja_massgeblich: "Ja, maßgeblich", teilweise: "Teilweise",
  nein: "Nein", noch_nicht_beurteilbar: "Noch nicht beurteilbar",
};
const F5_LABELS = { ja: "Ja", teilweise: "Teilweise", nein: "Nein" };

const COLORS = {
  vollzeit: "bg-green-500", teilzeit: "bg-emerald-400",
  minijob: "bg-yellow-400", ausbildung: "bg-blue-400",
  arbeitssuchend: "bg-orange-400", andere_massnahme: "bg-slate-400",
  ja_vollstaendig: "bg-green-500", ja_massgeblich: "bg-green-500",
  ja: "bg-green-500",
  teilweise: "bg-yellow-400",
  eher_nicht: "bg-orange-400", nein: "bg-red-400",
  nicht_zutreffend: "bg-slate-300", noch_nicht_beurteilbar: "bg-slate-300",
};

function VerteilungsBar({ distribution, labels, title }) {
  const total = Object.values(distribution || {}).reduce((a, b) => a + b, 0);
  if (!total) return (
    <Card>
      <CardHeader className="pb-3"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="text-sm text-muted-foreground">Noch keine Antworten</CardContent>
    </Card>
  );
  const entries = Object.entries(distribution).filter(([_, n]) => n > 0)
    .sort((a, b) => b[1] - a[1]);
  return (
    <Card data-testid={`verteilung-card-${title}`}>
      <CardHeader className="pb-3"><CardTitle className="text-base">{title}</CardTitle></CardHeader>
      <CardContent className="space-y-2">
        {entries.map(([k, n]) => {
          const pct = Math.round((n / total) * 100);
          return (
            <div key={k}>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-slate-700">{labels[k] || k}</span>
                <span className="font-medium text-slate-900">{n} ({pct}%)</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className={`h-full ${COLORS[k] || "bg-slate-400"} transition-all`}
                     style={{ width: `${pct}%` }} />
              </div>
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}

export default function VerbleibsbefragungAuswertung() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusInfo, setStatusInfo] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [{ data: ausw }, { data: status }] = await Promise.all([
        axios.get(`${API}/verbleibsbefragung/auswertung`),
        axios.get(`${API}/verbleibsbefragung/status`),
      ]);
      setData(ausw);
      setStatusInfo(status);
    } catch (e) {
      toast.error("Fehler beim Laden der Auswertung",
        { description: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const triggerRunNow = async () => {
    if (!window.confirm("Soll der Versand jetzt ausgelöst werden?\n\n" +
      `${statusInfo?.anstehend_count || 0} Teilnehmer im Fenster werden angeschrieben (keine Doppelversendung).`)) return;
    try {
      setRunning(true);
      const { data: res } = await axios.post(`${API}/verbleibsbefragung/run-now`, { force: false });
      toast.success(`Versand abgeschlossen`, {
        description: `${res.gesendet} versendet · ${res.uebersprungen} übersprungen · ${res.fehler} Fehler`,
      });
      await load();
    } catch (e) {
      toast.error("Fehler beim Versand",
        { description: e?.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="vbf-loading">
      <Loader2 className="w-6 h-6 animate-spin text-orange-500" />
    </div>
  );
  if (!data) return null;

  const stats = [
    { icon: Send, label: "Versendet", value: data.versendet_gesamt, color: "text-blue-600" },
    { icon: MessageSquareText, label: "Eingegangen", value: data.antworten_gesamt, color: "text-green-600" },
    { icon: TrendingUp, label: "Antwort-Quote", value: `${data.antwort_quote_prozent ?? 0}%`, color: "text-orange-600" },
    { icon: Award, label: "Ø Weiterempfehlung", value: data.weiterempfehlung_durchschnitt ?? "—",
      sub: data.weiterempfehlung_durchschnitt ? "(1 = sehr gerne, 5 = gar nicht)" : null, color: "text-purple-600" },
  ];

  const anstehendCount = statusInfo?.anstehend_count ?? 0;

  return (
    <div className="space-y-6" data-testid="verbleibsbefragung-auswertung">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-orange-500" />
            Verbleibsbefragung — Auswertung
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            AZAV-Wirkungsmessung · anonyme Erhebung 6 Monate nach Maßnahmeende
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Aktualisieren
          </Button>
          <Button onClick={triggerRunNow} disabled={running || anstehendCount === 0}
                  data-testid="run-now-btn" className="bg-orange-500 hover:bg-orange-600">
            {running ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
            Jetzt versenden ({anstehendCount})
          </Button>
        </div>
      </div>

      {/* Stats Top Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} data-testid={`stat-${s.label}`}>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <s.icon className={`w-8 h-8 ${s.color}`} />
                <span className="text-2xl font-bold text-slate-900">{s.value}</span>
              </div>
              <div className="text-sm text-slate-600 mt-2">{s.label}</div>
              {s.sub && <div className="text-xs text-slate-400 mt-0.5">{s.sub}</div>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Job Status Info */}
      {statusInfo && (
        <Card className="border-orange-200 bg-orange-50/40">
          <CardContent className="pt-6 text-sm">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
              <div className="flex items-center gap-2">
                <Circle className="w-3 h-3 fill-green-500 text-green-500" />
                <span className="text-slate-700">{statusInfo.schedule}</span>
              </div>
              {statusInfo.fenster && (
                <div className="text-slate-600">
                  Aktuelles Fenster: <b>{statusInfo.fenster.von}</b> bis <b>{statusInfo.fenster.bis}</b>
                </div>
              )}
              <div className="text-slate-600">
                Anstehend (AZAV-Bestandene mit Mail): <Badge variant="secondary">{anstehendCount}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Verteilungen */}
      <div className="grid lg:grid-cols-2 gap-4">
        <VerteilungsBar
          title="Aktuelle Erwerbstätigkeit"
          distribution={data.verteilung?.f1_erwerbstaetig}
          labels={F1_LABELS}
        />
        <VerteilungsBar
          title="Lerninhalte im Alltag anwendbar?"
          distribution={data.verteilung?.f2_anwendbarkeit}
          labels={F2_LABELS}
        />
        <VerteilungsBar
          title="Hat Maßnahme bei Stellensuche geholfen?"
          distribution={data.verteilung?.f3_stellensuche_geholfen}
          labels={F3_LABELS}
        />
        <VerteilungsBar
          title="Heute im Wunschberuf tätig?"
          distribution={data.verteilung?.f5_wunschberuf}
          labels={F5_LABELS}
        />
      </div>

      {/* Antworten nach Kurs-Kategorie */}
      {data.nach_kurs_kategorie && Object.keys(data.nach_kurs_kategorie).length > 0 && (
        <Card data-testid="nach-kategorie-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Briefcase className="w-4 h-4 text-slate-500" />
              Antworten nach Kurs-Kategorie
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {Object.entries(data.nach_kurs_kategorie).map(([cat, n]) => (
                <Badge key={cat} variant="secondary" className="text-sm py-1.5 px-3">
                  {cat}: <b className="ml-1">{n}</b>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Freitexte */}
      {data.freitexte && data.freitexte.length > 0 && (
        <Card data-testid="freitexte-card">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquareText className="w-4 h-4 text-slate-500" />
              Freitext-Antworten ({data.freitexte.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {data.freitexte.map((ft, idx) => (
                <div key={idx} className="border-l-2 border-orange-300 pl-4 py-1">
                  <p className="text-sm text-slate-800 whitespace-pre-wrap">{ft.text}</p>
                  <p className="text-xs text-slate-400 mt-1">
                    {ft.kurs_kategorie}{ft.kurs_ende_monat ? ` · Abschluss: ${ft.kurs_ende_monat}` : ""}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {data.antworten_gesamt === 0 && (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600 font-medium">Noch keine Antworten eingegangen</p>
            <p className="text-sm text-slate-400 mt-1">
              Sobald Teilnehmer ihre Befragung online ausgefüllt haben, erscheinen die anonymen Ergebnisse hier.
            </p>
          </CardContent>
        </Card>
      )}

      <ManuellerVersand onAfterSend={load} />
    </div>
  );
}


function ManuellerVersand({ onAfterSend }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/verbleibsbefragung/kandidaten`);
      setItems(data.items || []);
    } catch (e) {
      toast.error("Fehler beim Laden",
        { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  };

  useEffect(() => { if (expanded) load(); }, [expanded]);

  const filtered = showAll ? items : items.filter(t => t.ist_bestanden && !t.abgebrochen);

  const toggleSelect = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const toggleSelectAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(t => t.id)));
  };

  const send = async () => {
    if (selected.size === 0) { toast.error("Keine Teilnehmer ausgewählt"); return; }
    if (!window.confirm(`Verbleibsbefragung an ${selected.size} Teilnehmer versenden?`)) return;
    try {
      setSending(true);
      const { data: res } = await axios.post(`${API}/verbleibsbefragung/send-manual`, {
        teilnehmer_ids: Array.from(selected), force_override: false,
      });
      toast.success("Manueller Versand abgeschlossen", {
        description: `${res.gesendet} versendet · ${res.uebersprungen} übersprungen · ${res.fehler} Fehler`,
      });
      setSelected(new Set());
      await load();
      if (onAfterSend) await onAfterSend();
    } catch (e) {
      toast.error("Fehler beim Versand",
        { description: e?.response?.data?.detail || e.message });
    } finally { setSending(false); }
  };

  return (
    <Card data-testid="manueller-versand-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between cursor-pointer"
                   onClick={() => setExpanded(!expanded)}>
          <span className="flex items-center gap-2">
            <MailCheck className="w-5 h-5 text-orange-500" />
            Manueller Versand · gezielt einzelne Teilnehmer anschreiben
          </span>
          {expanded ? <ChevronUp className="w-5 h-5"/> : <ChevronDown className="w-5 h-5"/>}
        </CardTitle>
      </CardHeader>
      {expanded && (
      <CardContent className="space-y-4">
        <div className="bg-orange-50/50 border border-orange-200 rounded-lg p-3 text-sm text-slate-700">
          <AlertTriangle className="w-4 h-4 inline mr-1 text-orange-500" />
          <b>Tipp:</b> Diese Liste zeigt alle Teilnehmer, deren Kursende ≥ 6 Monate her ist,
          die eine E-Mail-Adresse haben und noch keine Verbleibsbefragung erhalten haben.
          Auch <b>Ninox-Importe ohne Kurs-Eintrag</b> werden angezeigt — Sie entscheiden manuell.
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}
                  data-testid="reload-kandidaten-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin":""}`} />
            Liste neu laden
          </Button>
          <label className="flex items-center gap-2 text-sm cursor-pointer"
                 data-testid="show-all-toggle">
            <input type="checkbox" checked={showAll}
                   onChange={(e) => setShowAll(e.target.checked)} className="rounded" />
            <span>Auch nicht-bestandene / abgebrochene anzeigen</span>
          </label>
          <div className="ml-auto text-sm text-slate-500">
            {selected.size} von {filtered.length} ausgewählt
          </div>
          <Button onClick={send} disabled={selected.size === 0 || sending}
                  className="bg-orange-500 hover:bg-orange-600" data-testid="send-selected-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin"/> : <Send className="w-4 h-4 mr-2"/>}
            Auswahl versenden ({selected.size})
          </Button>
        </div>

        {loading && filtered.length === 0 ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-orange-500"/></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-8 text-slate-500 text-sm">
            <CheckCircle2 className="w-10 h-10 text-slate-300 mx-auto mb-2"/>
            Keine offenen Teilnehmer im Versandfenster.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-slate-200">
            <table className="w-full text-sm" data-testid="kandidaten-table">
              <thead className="bg-slate-50 text-slate-700">
                <tr>
                  <th className="p-2 text-left">
                    <input type="checkbox"
                           checked={selected.size === filtered.length && filtered.length > 0}
                           onChange={toggleSelectAll}
                           className="rounded" data-testid="select-all-checkbox"/>
                  </th>
                  <th className="p-2 text-left font-medium">Name</th>
                  <th className="p-2 text-left font-medium">E-Mail</th>
                  <th className="p-2 text-left font-medium">Kurs</th>
                  <th className="p-2 text-left font-medium">Ende</th>
                  <th className="p-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id} className={`border-t border-slate-100 hover:bg-slate-50 ${selected.has(t.id) ? "bg-orange-50/40" : ""}`}
                      data-testid={`row-${t.id}`}>
                    <td className="p-2">
                      <input type="checkbox" checked={selected.has(t.id)}
                             onChange={() => toggleSelect(t.id)} className="rounded"
                             data-testid={`checkbox-${t.id}`}/>
                    </td>
                    <td className="p-2 font-medium text-slate-900">
                      {t.vorname} {t.nachname}
                    </td>
                    <td className="p-2 text-slate-600 truncate max-w-[200px]">{t.email}</td>
                    <td className="p-2 text-slate-600">
                      {t.kurs || <span className="italic text-slate-400">— (Ninox-Import)</span>}
                    </td>
                    <td className="p-2 text-slate-600">{t.kurs_ende}</td>
                    <td className="p-2">
                      <div className="flex flex-wrap gap-1">
                        {t.ist_azav && <Badge variant="outline" className="text-xs h-5 bg-green-50 text-green-700 border-green-200">AZAV</Badge>}
                        {t.ist_bestanden && <Badge variant="outline" className="text-xs h-5 bg-blue-50 text-blue-700 border-blue-200">bestanden</Badge>}
                        {t.abgebrochen && <Badge variant="outline" className="text-xs h-5 bg-red-50 text-red-700 border-red-200">abgebrochen</Badge>}
                        {!t.ist_bestanden && !t.abgebrochen && <Badge variant="outline" className="text-xs h-5 text-slate-500">unklar</Badge>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
      )}
    </Card>
  );
}

